import { processRequest } from '../../../services/Api';
import { eventChannel, END } from 'redux-saga';
import { all, call, put, take, select, cancelled, takeLatest } from 'redux-saga/effects';
import shortid from 'shortid';
import queryString from 'qs';
import { tutorChatTypes } from './Constants';
import { AI_CHAT_API_URL, BASE_API_URL, COOKIES, SOURCE_TYPES } from '../../../services/Constants';
import {
	getCookie,
	getLocalStorageItem,
	getSessionStorageItem,
	isSecure,
	setCookie,
	setLocalStorageItem,
} from '../../../services/Helper';
import { getAccessToken } from '../../../services/AccessToken';
import { aiChatData } from './Selectors';
import * as tutorChatActions from './Actions';
import * as errorHandlerActions from '../../errorHandler/Actions';
import { IS_ANDROID_APP, IS_IOS_APP } from '../../../services/capacitor/Constants';
import { appInfo } from '../../../services/capacitor/Helper';

const parseString = (chunk) => {
	return chunk
		.split('\n')
		.map((line) => line.replace('data: ', ''))
		.filter((data) => data.length > 0);
};

function createPostEventStream(url, message, token, setHeader, internalAPI) {
	let headers = {};
	if (internalAPI) {
		const { version: clientVersion } = appInfo || {};
		let clientType = 'web';

		if (IS_ANDROID_APP) {
			clientType = 'android';
		} else if (IS_IOS_APP) {
			clientType = 'ios';
		}
		headers = { ...headers, 'X-Auth-Token': getAccessToken(), 'X-Client-Type': clientType };

		if (clientVersion) headers = { ...headers, 'X-Client-Version': clientVersion };

		const selectedLanguage = getLocalStorageItem('selectedLanguage');
		if (selectedLanguage) headers = { ...headers, 'X-Language-Code': selectedLanguage };

		const selectedUserLocale = getLocalStorageItem('selectedLocale');
		if (selectedUserLocale) headers = { ...headers, 'X-Locale-Code': selectedUserLocale };

		// set sessionId for Amplitude
		const sessionId = getSessionStorageItem('sessionId');
		if (sessionId) headers = { ...headers, 'X-Session-Id': sessionId };

		// set deviceId for Amplitude
		const deviceId = getLocalStorageItem('deviceId');
		if (deviceId) headers = { ...headers, 'X-Device-Id': deviceId };
	} else {
		headers = { Authorization: `Bearer ${token}` };
	}

	return eventChannel((emit) => {
		const controller = new AbortController();
		const { signal } = controller || {};

		fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'text/event-stream',
				...headers,
			},
			body: JSON.stringify(message),
			signal,
		})
			.then((response) => {
				if (response.status === 401) {
					emit({ type: 'error', error: { status: response.status } });
					return;
				}
				if (!response.body) {
					throw new Error('No response body for streaming.');
				}

				if (setHeader && response.headers) setHeader(response.headers);

				const reader = response.body.getReader();
				const decoder = new TextDecoder('utf-8');

				const readStream = () => {
					reader.read().then(({ done, value }) => {
						if (done) {
							emit(END);
							return;
						}
						const chunk = decoder.decode(value, { stream: true });
						const parsedDataArray = parseString(chunk);

						parsedDataArray.forEach((data) => {
							try {
								const jsonData = JSON.parse(data);
								emit({ type: 'message', data: jsonData });
							} catch (err) {
								emit({ type: 'message', data });
							}
						});

						readStream();
					});
				};

				readStream();
			})
			.catch((error) => {
				emit({ type: 'error', error });
				emit(END);
			});

		return () => {
			controller.abort();
		};
	});
}

function* refreshAiToken(data) {
	const { callback, callbackData } = data || {};
	const currentData = yield select(aiChatData);
	const { chosenDepartment, chosenDepartmentTutorAvailable, source } = currentData || {};
	const { subject, topicId, streamId, bookId, exerciseId, name } = source || {};
	const isTopicPage = name === SOURCE_TYPES.TOPIC_DETAIL;

	try {
		const { data: respData } = yield call(
			processRequest,
			'ai_service_token/refresh?service=ai_tutor',
		);

		setCookie(COOKIES.chatAccessToken, respData.access_token, { secure: isSecure() });
		setLocalStorageItem('ai-chat-timestamp', new Date().toISOString());

		switch (callback) {
			case 'history':
				yield put(tutorChatActions.getAiChatHistory(callbackData));
				break;
			case 'message':
				yield put(tutorChatActions.aiConversation(callbackData));
				break;
			case 'analytics':
				yield put(tutorChatActions.aiTutorAnalytics(callbackData));
				break;
			default:
				break;
		}
	} catch (e) {
		if (e.response && e.response.status === 401) {
			yield put(
				tutorChatActions.aiTutorInit({
					subject: chosenDepartment || subject?.name,
					topicId,
					streamId,
					bookId,
					isTopicPage,
					exerciseId,
					tutorAvailable: chosenDepartmentTutorAvailable,
					preventLoadHistory: callback !== 'history',
					callback,
					callbackData,
				}),
			);
		} else {
			yield put(errorHandlerActions.handleError(e, 'notification'));
		}
	}
}

export default function* () {
	yield all([
		takeLatest(tutorChatTypes.AI_CHAT_INIT, handleAiChatAuth),
		takeLatest(tutorChatTypes.GET_AI_CHAT_HISTORY, handleGetAiChatHistory),
		takeLatest(tutorChatTypes.AI_CONVERSATION, handleAiConversation),
		takeLatest(tutorChatTypes.AI_TUTOR_ANALYTICS, handleAiTutorAnalytics),
	]);
}

export function* handleAiChatAuth({ payload }) {
	const { data } = payload || {};
	const {
		subject,
		topicId: topic_id,
		streamId: stream_id,
		bookId: book_id,
		isTopicPage,
		tutorAvailable: tutor_available,
		preventLoadHistory,
		callback,
		callbackData,
		exerciseId: exercise_id,
	} = data || {};

	const queryObjString = queryString.stringify({
		subject,
		tutor_available,
		topic_id,
		stream_id,
		book_id,
		exercise_id,
	});

	try {
		const { data: respData } = yield call(
			processRequest,
			`ai_service_token/auth?service=ai_tutor&${queryObjString}`,
		);

		setCookie(COOKIES.chatAccessToken, respData.access_token, { secure: isSecure() });
		setLocalStorageItem('ai-chat-timestamp', new Date().toISOString());

		yield put(tutorChatActions.aiTutorInitSuccess());

		if (!preventLoadHistory)
			yield put(tutorChatActions.getAiChatHistory({ isTopicPage, ...(callbackData || {}) }));

		if (typeof callback === 'function') {
			callback(callbackData);
			return;
		}

		switch (callback) {
			case 'message':
				yield put(tutorChatActions.aiConversation(callbackData));
				break;
			case 'analytics':
				yield put(tutorChatActions.aiTutorAnalytics(callbackData));
				break;
			default:
				break;
		}
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(tutorChatActions.aiTutorInitError());
	}
}

export function* handleGetAiChatHistory({ payload }) {
	const { data: requestData } = payload || {};
	const {
		isTopicPage: topic_page,
		last_evaluated_key,
		prevHistory,
		supportCallback,
	} = requestData || {};

	try {
		const { data } = yield call(
			processRequest,
			`${AI_CHAT_API_URL}/conversation-history`,
			'POST',
			{ topic_page, last_evaluated_key },
			false,
			false,
			{
				Authorization: `Bearer ${getCookie(COOKIES.chatAccessToken)}`,
			},
			true,
		);

		const { resultsId, internal } = (yield select(aiChatData)).source || {};

		let internalHistoryData = null;

		if (!prevHistory && resultsId && internal) {
			internalHistoryData = yield call(
				processRequest,
				`conversation-history?results_id=${resultsId}`,
			);
			if (internalHistoryData?.data?.history?.length)
				yield put(tutorChatActions.setAiChatModal({ internalAPI: true }));
		}

		yield put(
			tutorChatActions.getAiChatHistorySuccess({
				result: internalHistoryData?.data?.history?.length
					? {
							...data,
							history: [...data.history, ...internalHistoryData.data.history],
					  }
					: data,
				isHistory: !!last_evaluated_key,
			}),
		);

		if (supportCallback) supportCallback();
	} catch (e) {
		if (e.response && e.response.status === 401) {
			yield call(refreshAiToken, { callback: 'history', callbackData: { ...requestData } });
		}
		yield put(tutorChatActions.getAiChatHistoryError());
	}
}

export function* handleAiConversation({ payload }) {
	const { data: requestData } = payload || {};
	const {
		message,
		aiMessage,
		topic_id,
		book_id,
		stream_id,
		subject,
		question,
		callback,
		isTopicPage,
		internalAPI,
		results_id,
	} = requestData || {};

	const formattedData = {
		message: { tutor: aiMessage, visitor: message },
		topic_id,
		book_id,
		subject,
		tutorName: 'studyGO',
		userType: 'tutor',
		new_tutor: true,
		isTopicPage,
		question,
		results_id,
	};

	let authCall;
	let sessionId;
	let showDivider;
	let isError;
	let successFinish = true;
	const addedOn = new Date().toISOString();
	const objectId = shortid.generate();

	if (internalAPI) yield put(tutorChatActions.setAiChatModal({ internalAPI }));

	const channel = yield call(
		createPostEventStream,
		`${internalAPI ? BASE_API_URL : `${AI_CHAT_API_URL}/`}conversation`,
		formattedData,
		getCookie(COOKIES.chatAccessToken),
		(headers) => {
			authCall = headers.get('auth-call');
			sessionId = headers.get('session-id');
			showDivider = headers.get('show-divider') === '1';
			isError = headers.get('is-error') === '1' || undefined;
		},
		internalAPI,
	);

	try {
		while (true) {
			const resp = yield take(channel);
			const { type, data, error } = resp || {};

			if (type === 'message') {
				yield put(
					tutorChatActions.eventAiStreamMessage({
						show_divider: showDivider,
						is_error: isError,
						text: data,
						session_id: sessionId,
						added_on: addedOn,
						id: objectId,
					}),
				);
			} else if (type === 'error') {
				if (error.status === 401) {
					successFinish = false;
					yield call(refreshAiToken, { callback: 'message', callbackData: requestData });
				} else {
					yield put(errorHandlerActions.handleError(error, 'notification'));
					yield put(tutorChatActions.aiConversationError(error));
				}
			}
		}
	} finally {
		if (successFinish) {
			yield put(tutorChatActions.stopAiEventStream({ authCall }));
		}
		if (callback) callback();

		if (authCall === 'immediate' && topic_id) {
			yield put(
				tutorChatActions.aiTutorInit({
					subject,
					topicId: topic_id,
					streamId: stream_id,
					bookId: book_id,
					isTopicPage: true,
					tutorAvailable: false,
					preventLoadHistory: true,
					preventLoading: true,
				}),
			);
		}

		if (yield cancelled()) {
			channel.close();
		}
	}
}

export function* handleAiTutorAnalytics({ payload }) {
	const { data } = payload || {};

	const authKey = getCookie(COOKIES.chatAccessToken);

	if (!authKey) return;

	try {
		yield call(
			processRequest,
			`${AI_CHAT_API_URL}/analytics`,
			'POST',
			data,
			false,
			false,
			{
				'Content-Type': 'text/event-stream',
				Authorization: `Bearer ${authKey}`,
			},
			true,
		);
	} catch (e) {
		if (e.response && e.response.status === 401) {
			yield call(refreshAiToken, { callback: 'analytics', callbackData: data });
		}
	}
}
